import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import '../../styles/components/FormPage.css';
import { Input as FormInput, Select as FormSelect } from '../FormField';
import RichTextEditor from '../ui/RichTextEditor';
import { GENRES, SONG_TYPES } from '../MusicView';
import { Song, addSong } from '../../services/SongsService';
import { useFormSaveEvent, firstMissingField, reportSaveError, useFieldErrors } from './_shared';
import { useNotifications } from '../NotificationProvider';

interface SongFormPageProps {
  onSave: () => void;
  onCancel: () => void;
  onValidityChange?: (isValid: boolean) => void;
}

export const SongFormPage = forwardRef<{ save: () => void }, SongFormPageProps>(({ onSave, onCancel, onValidityChange }, ref) => {
  const { showError } = useNotifications();
  const fieldErrors = useFieldErrors();
  const [form, setForm] = useState({
    name: '', artist: '', genre: 'Afrobeats' as string,
    type: 'After Party' as string, link: '', notes: '',
  });

  const canSave = form.name.trim().length > 0;

  useEffect(() => {
    onValidityChange?.(canSave);
  }, [canSave, onValidityChange]);

  const handleSave = useCallback(async () => {
    const missing = firstMissingField([{ label: 'Song Name', value: form.name }]);
    if (missing) { showError(missing); return; }
    fieldErrors.clear();
    const song: Song = {
      name: form.name.trim(),
      artist: form.artist.trim(),
      genre: form.genre,
      type: form.type,
      ...(form.link.trim() ? { link: form.link.trim() } : {}),
      ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
    };
    try {
      await addSong(song);
    } catch (err) {
      // Don't call onSave — it navigates away, which used to strand the user
      // on the playlist believing a song had been added that never was.
      reportSaveError(err, fieldErrors, showError, 'Failed to add song.');
      return;
    }
    onSave();
  }, [canSave, form, onSave, showError, fieldErrors]);

  useImperativeHandle(ref, () => ({ save: handleSave }), [handleSave]);
  useFormSaveEvent(handleSave);

  return (
    <div className="inline-form-page">
      <div className="form-row">
        <FormInput label="Song Name" type="text" value={form.name}
          error={fieldErrors.errorFor('name')}
          onChange={e => { setForm({ ...form, name: e.target.value }); fieldErrors.clear('name'); }}
          placeholder="e.g. Holy Ghost" className="sfp-form-group-flex2" required />
        <FormInput label="Artist" type="text" value={form.artist}
          error={fieldErrors.errorFor('artist')}
          onChange={e => { setForm({ ...form, artist: e.target.value }); fieldErrors.clear('artist'); }} placeholder="e.g. Omah Lay" />
      </div>
      <div className="form-row">
        <FormSelect label="Genre" value={form.genre}
          onChange={e => setForm({ ...form, genre: e.target.value })}
          options={GENRES.map(g => ({ value: g, label: g }))} />
        <FormSelect label="Playlist" value={form.type}
          onChange={e => setForm({ ...form, type: e.target.value })}
          options={SONG_TYPES.map(t => ({ value: t, label: t }))} />
      </div>
      <FormInput label="YouTube / Spotify Link" type="url" value={form.link}
        error={fieldErrors.errorFor('link')}
        onChange={e => { setForm({ ...form, link: e.target.value }); fieldErrors.clear('link'); }} placeholder="https://..." />
      <RichTextEditor label="Notes" value={form.notes}
        onChange={val => setForm({ ...form, notes: val })} rows={3}
        placeholder="e.g. Instrumental only, play during entrance..." />
    </div>
  );
});

SongFormPage.displayName = 'SongFormPage';
